"use client";
import React, { useState, useEffect } from "react";
import { ContentContainer, Layout, Stack } from "./molecules";
import DemandGrid from "./DemandGrid";
import { getTrendings } from "../actions/singles";
import { getRelatedCourse } from "../actions/clientSide";
import DemandCourseGridSkeleton from "../components/DemandCourseGridSkeleton";
import { useDictionary } from "../context/dictionaryProvider";
import { flattenAttributes } from "../lib/util";
import { getPaginatedCategoryCourses } from "../actions/clientSide/categoryCourses";

const handleCourseFetching = async (
  key = "related",
  params,
  category,
  showRelated = false
) => {
  const categorySlug =
    category?.category?.data?.attributes?.slug ||
    category?.data?.attributes?.slug ||
    category?.slug ||
    "";
  
  let onDemandCourses;

  switch (key) {
    case "related":
      onDemandCourses = await getRelatedCourse(
        { ...params, showRelated },
        categorySlug
      );
      break;

    case "trending":
      onDemandCourses = await getTrendings(params);
      break;

    default:
      onDemandCourses = [];
  }

  return onDemandCourses;
};

const normalizeCourses = (coursesData) => {
  if (!coursesData) return [];

  const rawCourses = Array.isArray(coursesData)
    ? coursesData
    : coursesData?.data || [];

  return rawCourses.map((course) => flattenAttributes(course));
};

const DemandCoursesGrid = ({
  title,
  by,
  params,
  category,
  showRelated,
  coursesData,
  coursePathPrefix = "",
  categoryTypeSlug,
  isPCategory = false,
}) => {
  const [courses, setCourses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const dict = useDictionary();
  const hasDirectCourses = coursesData !== undefined && coursesData !== null;

  const INITIAL_DISPLAY_COUNT = 8;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (categoryTypeSlug) {
          const response = await getPaginatedCategoryCourses({
            categorySlug: categoryTypeSlug,
            lang: params?.lang,
            page: 1,
            pageSize: INITIAL_DISPLAY_COUNT,
            isPCategory,
          });
          setCourses(response?.data || []);
          setPage(1);
          setPageCount(response?.meta?.pagination?.pageCount || 1);
          return;
        }

        if (hasDirectCourses) {
          setCourses(normalizeCourses(coursesData));
          return;
        }

        const response = await handleCourseFetching(
          by,
          params,
          category,
          showRelated
        );
        setCourses(response?.data || []);
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [by, params, category, showRelated, hasDirectCourses, coursesData, categoryTypeSlug, isPCategory]);

  const isPaginatedCategory = Boolean(categoryTypeSlug);
  const displayedCourses = isPaginatedCategory
    ? courses
    : showAll
      ? courses
      : courses?.slice(0, INITIAL_DISPLAY_COUNT);

  const hasMoreCourses = isPaginatedCategory
    ? page < pageCount
    : courses?.length > INITIAL_DISPLAY_COUNT;

  const handleShowMore = async () => {
    if (isPaginatedCategory) {
      const nextPage = page + 1;
      try {
        setLoadingMore(true);
        const response = await getPaginatedCategoryCourses({
          categorySlug: categoryTypeSlug,
          lang: params?.lang,
          page: nextPage,
          pageSize: INITIAL_DISPLAY_COUNT,
          isPCategory,
        });
        setCourses((current) => [...(current || []), ...(response?.data || [])]);
        setPage(nextPage);
        setPageCount(response?.meta?.pagination?.pageCount || pageCount);
      } catch (error) {
        console.error("Error loading more category courses:", error);
      } finally {
        setLoadingMore(false);
      }
      return;
    }
    setShowAll(true);
  };

  const handleShowLess = () => {
    setShowAll(false);
    window.scrollTo({
      top: document.querySelector('.demandCoursesGrid')?.offsetTop - 100 || 0,
      behavior: 'smooth'
    });
  };

  // Don't render anything if loading or no courses
  if (loading) {
    return (
      <Layout className="demandCoursesGrid">
        <ContentContainer>
          <Stack>
            {title && (
              <h3 className="text-center w-full mb-11 text-[32px] leading-[32px] md:text-[40px] md:leading-[48px] font-extrabold">
                {title}
              </h3>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <DemandCourseGridSkeleton key={i} />
              ))}
            </div>
          </Stack>
        </ContentContainer>
      </Layout>
    );
  }

  // Don't render if no courses
  if (!courses || courses.length === 0) {
    return null;
  }

  return (
    <Layout className="demandCoursesGrid">
      <ContentContainer>
        <Stack>
          {/* Only show title if provided or from dict */}
          {(title || dict?.demandCourses?.title) && (
            <h3 className="text-center w-full mb-11 text-[32px] leading-[32px] md:text-[40px] md:leading-[48px] font-extrabold">
              {title || dict?.demandCourses?.title}
            </h3>
          )}
          
          <DemandGrid courses={displayedCourses} coursePathPrefix={coursePathPrefix} />

          {/* Show More / Show Less Button - Only if more courses exist */}
          {hasMoreCourses && (
            <div className="w-full text-center mt-8">
              {isPaginatedCategory || !showAll ? (
                <button
                  onClick={handleShowMore}
                  disabled={loadingMore}
                  data-track="load_more"
                  data-track-category={categorySlug}
                  data-track-shown-count={displayedCourses?.length}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors duration-300 shadow-md hover:shadow-lg"
                >
                  {loadingMore ? "Loading..." : "Show More Courses"}
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={handleShowLess}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-gray-900 text-gray-900 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-300 shadow-md hover:shadow-lg"
                >
                  Show Less
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
        </Stack>
      </ContentContainer>
    </Layout>
  );
};

export default DemandCoursesGrid;
